/**
 * Authoritative ingredient identity resolver backed by the EU Commission's
 * CosIng cosmetic-ingredient inventory (~28k substances).
 *
 * SOTA cosmetic regulatory platforms resolve raw ingredient text → canonical
 * INCI + CAS via an exact lookup against this inventory's name & CAS columns
 * BEFORE doing any fuzzy fallback. That eliminates the silent-rewrite class
 * of bugs (e.g. "Ethanol" → "Ethylene glycol monomethyl ether and its
 * acetate") that the previous fuzzy-only matcher produced for any input
 * lacking an IFRA Standard.
 *
 * Behaviour:
 *   • CAS hit → confidence 1.0, matchedBy: "cas".
 *   • Exact-name hit (after normalisation) → confidence 1.0, matchedBy: "name".
 *   • Otherwise → null. Caller decides whether to fuzzy-fallback (with its
 *     own confidence guard).
 */

import cosingIndex from "../data/cosing_index.json";

const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const normCas = (s) => {
  const m = String(s || "").match(/\d{2,7}-\d{2}-\d/);
  return m ? m[0] : "";
};

const buildHit = (key, matchedBy) => {
  const sub = cosingIndex.substances[key];
  if (!sub) return null;
  return {
    inciName: sub.inci,
    casNumbers: sub.cas || [],
    ecNumber: sub.ec || "",
    function: sub.fn || "",
    cosingKey: key,
    confidence: 1.0,
    matchedBy,
    source: cosingIndex._meta.source,
    sourceUrl: cosingIndex._meta.sourceUrl,
  };
};

export const resolveCosingByCas = (cas) => {
  const c = normCas(cas);
  if (!c) return null;
  const key = cosingIndex.byCas[c];
  return key ? buildHit(key, "cas") : null;
};

export const resolveCosingByName = (name) => {
  const n = normName(name);
  if (!n) return null;
  const key = cosingIndex.byName[n];
  return key ? buildHit(key, "name") : null;
};

// Combined entry point used by fuzzySearchUtils. Tries CAS first because
// it's far more reliable, then exact name. Returns `null` when neither
// matches — fuzzy fallback is the caller's responsibility.
export const resolveCosing = ({ name = "", cas = "" } = {}) =>
  resolveCosingByCas(cas) || resolveCosingByName(name);

export const cosingMeta = cosingIndex._meta;
